/**
 * BM-H640 (27 Sep 2026): the Compliance group on the Today panel.
 *
 * Reads GET /api/compliance/my-tasks (BM-H636, backend/compliance/my_tasks.py):
 * the signed-in person's open compliance tasks, already sorted overdue first.
 *
 * A failed or malformed reply is an error, never an empty list: an empty
 * Compliance group would read as "nothing to do" when nothing was read.
 */

export const COMPLIANCE_TASKS_PATH = "/compliance/my-tasks";

/** Turn the endpoint's reply into { tasks, count, overdue }, or throw. */
export function complianceFromReply(reply) {
  const tasks = reply && reply.tasks;
  if (!Array.isArray(tasks)) {
    throw new Error("The compliance tasks reply had no task list.");
  }
  const usable = tasks.filter((t) => t && typeof t.title === "string" && t.title.trim());
  return {
    tasks: usable,
    count: usable.length,
    overdue: usable.filter((t) => t.overdue === true).length,
  };
}

/** The second line under a task: matter and due date. */
export function complianceTaskDetail(task) {
  const parts = [];
  if (task.matter_ref) parts.push(task.matter_ref);
  if (task.overdue && task.due) parts.push(`Overdue, was due ${task.due}`);
  else if (task.overdue) parts.push("Overdue");
  else if (task.due) parts.push(`Due ${task.due}`);
  return parts.join(" · ");
}

/** Severity dot: overdue is always the strongest. */
export function complianceTaskSeverity(task) {
  if (task.overdue) return "critical";
  return ["critical", "high", "medium", "low"].includes(task.severity) ? task.severity : "medium";
}

/** Only follow in-app links the endpoint gives; never an outside address. */
export function complianceTaskLink(task) {
  const link = typeof task.link === "string" ? task.link.trim() : "";
  return link.startsWith("/") && !link.startsWith("//") ? link : null;
}

/** Fetch and shape the tasks. Resolves { tasks, count, overdue } or { error }. */
export async function fetchComplianceTasks(get, api, headers) {
  try {
    const res = await get(`${api}${COMPLIANCE_TASKS_PATH}`, { headers });
    return complianceFromReply(res && res.data);
  } catch (err) {
    const status = err && err.response && err.response.status;
    const detail = err && err.response && err.response.data && err.response.data.detail;
    return {
      error: status
        ? `Could not load compliance tasks (${status})${detail ? `: ${detail}` : ""}`
        : `Could not load compliance tasks: ${(err && err.message) || "unknown error"}`,
    };
  }
}
